import { useState } from 'react';
import { useInterval } from 'react-use';

import {
  ChronoUnit,
  DateTimeFormatter,
  Duration,
  Instant,
  LocalDate,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';

import { assertNotEmpty } from 'shared/helpers/globals';

/** Formats a time like "11:59 PM". */
export const TIME_ONLY_FORMAT = DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US);

/** Formats a time like "12 PM". */

export const TIME_HOUR_ONLY_FORMAT = DateTimeFormatter.ofPattern('h a').withLocale(Locale.US);

/**
 * Formats a time like "11:59 PM PT".
 *
 * For some time zones like "America/Phoenix", js-joda will directly output "America Phoenix".
 * TODO: Figure out how to format it into MT/PT?
 */
export const TIME_WITH_TZ_FORMAT = DateTimeFormatter.ofPattern('h:mm a z').withLocale(Locale.US);

/** Needed to turn America/Chicago to CT. */
export const TZ_FORMAT = DateTimeFormatter.ofPattern('z').withLocale(Locale.US);

/** Formats a date like "Sun 11:59 PM". */
export const DAY_OF_WEEK_AND_TIME = DateTimeFormatter.ofPattern('EEE h:mm a').withLocale(Locale.US);

/** Formats a date like "Sunday". */
export const FULL_DAY_OF_WEEK_FORMAT = DateTimeFormatter.ofPattern('EEEE').withLocale(Locale.US);

/** Formats a date like "Jan 1". */
export const MONTH_DAY_FORMAT = DateTimeFormatter.ofPattern('MMM d').withLocale(Locale.US);

/** Formats a date like "Sun, Jan 1". */
export const MONTH_DAY_AND_DAY_OF_WEEK_FORMAT = DateTimeFormatter.ofPattern(
  'EEE, MMM d',
).withLocale(Locale.US);

/** Formats a date like "Jan". */
export const MONTH_ONLY_FORMAT = DateTimeFormatter.ofPattern('MMM').withLocale(Locale.US);

/** Formats a date like "Jan 2022". */
export const MONTH_YEAR_FORMAT = DateTimeFormatter.ofPattern('MMM yyyy').withLocale(Locale.US);

/** Formats a date like "1/1/2022". */
export const SHORT_FULL_DATE_FORMAT = DateTimeFormatter.ofPattern('M/d/yyyy').withLocale(Locale.US);

/** Formats a date like "Jan 1, 2022". */
export const SHORTER_FULL_DATE_FORMAT = DateTimeFormatter.ofPattern('MMM d, yyyy').withLocale(
  Locale.US,
);

/** Formats a date like "January 1, 2022". */
export const FULL_DATE_FORMAT = DateTimeFormatter.ofPattern('MMMM d, yyyy').withLocale(Locale.US);

/** Formats a date like "Sunday, January 1, 2022". */
export const FULL_DATE_AND_DAY_OF_WEEK_FORMAT = DateTimeFormatter.ofPattern(
  'EEEE, MMMM d, yyyy',
).withLocale(Locale.US);

/** Month day time format Jan 1, 10:30 AM EST */
export const MONTH_DAY_TIME_FORMAT = DateTimeFormatter.ofPattern('MMM d, h:mm a z').withLocale(
  Locale.US,
);
/** Month day time format Jan 1, 10:30 AM */
export const MONTH_DAY_TIME_FORMAT_WITHOUT_TIME_ZONE = DateTimeFormatter.ofPattern(
  'MMM d, h:mm a',
).withLocale(Locale.US);
/** Converts a JS Date to a {@link LocalDate} in the system time zone. */
export const localDateFromJsDate = (date: Date): LocalDate => {
  return Instant.ofEpochMilli(date.getTime()).atZone(ZoneId.systemDefault()).toLocalDate();
};

/** GraphQL Date interface. In the future, perhaps replace this with a fragment. */
export interface GqlDate {
  __typename: 'OdProtosCommon_Date';
  year: number;
  month: number;
  day: number;
}

/** Converts a GraphQL Date to a {@link LocalDate}. */
export const localDateFromGqlDate = ({ year, month, day }: GqlDate) => {
  return LocalDate.of(year, month, day);
};

/** Converts most ISO-8601 strings to {@link LocalDate} or errors if input is null-ish. */
export function localDateFromString(input: string | null | undefined): LocalDate {
  return LocalDate.parse(assertNotEmpty('input', input));
}

/** Converts most ISO-8601 strings to {@link LocalDate} or null if input is null-ish. */
export function localDateFromStringOrNull(input: string | null | undefined): LocalDate | null {
  if (!input) {
    return null;
  }
  return LocalDate.parse(input);
}

/** Converts a {@link LocalDate} to a GQL date. */
export const localDateToGqlDate = (date: LocalDate): GqlDate => {
  return {
    __typename: 'OdProtosCommon_Date',
    year: date.year(),
    month: date.monthValue(),
    day: date.dayOfMonth(),
  };
};

/** Converts most ISO-8601 strings to {@link Instant} or errors if input is null-ish. */
export function instantFromString(input: string | null | undefined): Instant {
  // Don't use Instant.parse, which only parses ISO-8601 strings in UTC time (i.e. end in "Z").
  return ZonedDateTime.parse(assertNotEmpty('input', input)).toInstant();
}

/** Converts most ISO-8601 strings to {@link Instant} or null if input is null-ish. */
export function instantFromStringOrNull(input: string | null | undefined): Instant | null {
  if (!input) {
    return null;
  }
  // Don't use Instant.parse, which only parses ISO-8601 strings in UTC time (i.e. end in "Z").
  return ZonedDateTime.parse(input).toInstant();
}

/** Converts most ISO-8601 strings to {@link ZonedDateTime} or errors if input is null-ish. */
export function zonedDateTimeFromString(
  input: string | null | undefined,
  zoneId: ZoneId,
): ZonedDateTime {
  return ZonedDateTime.parse(assertNotEmpty('input', input)).withZoneSameInstant(zoneId);
}

/** Converts most ISO-8601 strings to {@link ZonedDateTime} or null if input is null-ish. */
export function zonedDateTimeFromStringOrNull(
  input: string | null | undefined,
  zoneId: ZoneId,
): ZonedDateTime | null {
  if (!input) {
    return null;
  }
  return ZonedDateTime.parse(input).withZoneSameInstant(zoneId);
}

/**
 * Converts most ISO-8601 strings to {@link ZonedDateTime} in default system zone
 * or errors if input is null-ish.
 */
export function systemZonedDateTimeFromString(input: string | null | undefined): ZonedDateTime {
  return ZonedDateTime.parse(assertNotEmpty('input', input)).withZoneSameInstant(
    ZoneId.systemDefault(),
  );
}

/**
 * Converts most ISO-8601 strings to {@link ZonedDateTime} in default system zone
 * or null if input is null-ish.
 */
export function systemZonedDateTimeFromStringOrNull(
  input: string | null | undefined,
): ZonedDateTime | null {
  if (!input) {
    return null;
  }
  return ZonedDateTime.parse(input).withZoneSameInstant(ZoneId.systemDefault());
}

const MINUTE_S = ChronoUnit.MINUTES.duration().seconds();
const HOUR_S = ChronoUnit.HOURS.duration().seconds();
const DAY_S = ChronoUnit.DAYS.duration().seconds();
const MONTH_S = ChronoUnit.MONTHS.duration().seconds();
const YEAR_S = ChronoUnit.YEARS.duration().seconds();

/**
 * Formats a {@link Duration} into a human-friendly text that approximates the duration.
 */
export function formatApproxDuration(duration: Duration) {
  const seconds = duration.seconds();
  if (seconds > 2 * YEAR_S) {
    return `${Math.floor(seconds / YEAR_S)} years`;
  } else if (seconds >= YEAR_S) {
    return 'a year';
  } else if (seconds >= 2 * MONTH_S) {
    return `${Math.floor(seconds / MONTH_S)} months`;
  } else if (seconds >= MONTH_S) {
    return 'a month';
  } else if (seconds >= 2 * DAY_S) {
    return `${Math.floor(seconds / DAY_S)} days`;
  } else if (seconds >= DAY_S) {
    return 'a day';
  } else if (seconds >= 2 * HOUR_S) {
    return `${Math.floor(seconds / HOUR_S)} hours`;
  } else if (seconds >= HOUR_S) {
    return 'an hour';
  } else if (seconds >= 2 * MINUTE_S) {
    return `${Math.floor(seconds / MINUTE_S)} minutes`;
  } else if (seconds >= MINUTE_S) {
    return 'a minute';
  }

  return 'a few seconds';
}

/** Gets current hour in 24hr format */
export function getCurrentHourInTimezone(timeZone = 'America/Los_Angeles') {
  const formatter = new Intl.DateTimeFormat([], { timeZone, hour: 'numeric', hour12: false });
  const time = Number(formatter.format(Date.now()));
  // alert(new Date(Date.now()));
  return time;
}

/** Checks whether current time is within business hours */
export function checkIsInBusinessHours(
  timeZone = 'America/Los_Angeles',
  startHour = 8,
  endHour = 18,
) {
  const time = getCurrentHourInTimezone(timeZone);
  const isInBusinessHours = time >= startHour && time < endHour;
  return isInBusinessHours;
}

/** Watches checkIsInBusinessHours */
export function useCheckIsInBusinessHours(
  timeZone = 'America/Los_Angeles',
  startHour = 8,
  endHour = 18,
) {
  const [isInBusinessHours, setIsInBusinessHours] = useState(
    checkIsInBusinessHours(timeZone, startHour, endHour),
  );
  useInterval(() => {
    setIsInBusinessHours(checkIsInBusinessHours(timeZone, startHour, endHour));
  }, 60_000);
  return isInBusinessHours;
}

/** Format the date as "X days" */
export function formatDateAsDaysUntil(date: LocalDate) {
  const today = LocalDate.now();
  const daysDifference = today.until(date).days();
  if (daysDifference <= 0) {
    return '';
  } else if (daysDifference === 1) {
    return '1 day';
  }
  return `${daysDifference} days`;
}
